import firebase from 'firebase/compat/app';
import firebaseConfig from './firebaseConfig';
import 'firebase/compat/auth';
import 'firebase/compat/firestore';
import { authErrorCodes } from '../consts/general';
import { FirebaseError } from '@firebase/util';

// Below this the clock never reads pendingKlydos, so a klydo queued for it would
// never arrive. Collections don't go through that queue, so they have no floor.
export const PENDING_KLYDO_MIN_VERSION = 606.9;
const PREVIEW_TTL_MINUTES = 720; // 12 hours

class FirebaseService {
  db: firebase.firestore.Firestore;

  private auth: firebase.auth.Auth;

  constructor() {
    firebase.initializeApp(firebaseConfig);
    this.db = firebase.firestore();
    this.auth = firebase.auth();
  }

  private handleAuthError(error: FirebaseError, context: string = 'operation') {
    const isUserError = authErrorCodes.has(error.code);

    window.alert(error.message || `An error occurred during ${context}.`);

    if (!isUserError) {
      console.error(
        `Unexpected error during ${context}:`,
        error.code,
        error.message,
      );
    }
  }

  public onAuthStateChanged(callback: (user: firebase.User | null) => void) {
    return this.auth.onAuthStateChanged(callback);
  }

  public getToken() {
    return this.auth.currentUser?.getIdToken();
  }

  public impersonate(idToken: string) {
    return this.auth.signInWithCustomToken(idToken);
  }

  public getUserId() {
    return this.auth.currentUser?.uid;
  }

  public resetUser() {
    this.auth.currentUser?.getIdToken(true);
  }

  public signOut() {
    return this.auth.signOut();
  }

  public async signInWithGoogle() {
    if (!this.auth) {
      throw new Error('Firebase Auth is not initialized.');
    }

    const provider = new firebase.auth.GoogleAuthProvider();
    return this.auth.signInWithPopup(provider);
  }

  public async createUserWithEmailAndPassword(email: string, password: string) {
    try {
      const userCredential = await this.auth.createUserWithEmailAndPassword(
        email,
        password,
      );
      // The verification email is sent by the backend (branded Klaviyo flow),
      // triggered by the caller after the user is created.
      return userCredential.user;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      this.handleAuthError(error, 'registration');
    }
  }

  public async signInWithEmailAndPassword(email: string, password: string) {
    try {
      const userCredential = await this.auth.signInWithEmailAndPassword(
        email,
        password,
      );

      const user = userCredential.user;

      if (user && !user.emailVerified) {
        window.alert(
          'Your email is not verified. Please click resend verification email.',
        );
      }
      return user;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      this.handleAuthError(error, 'sign-in');
    }
  }

  // Clock command methods
  public async previewKlydoOnClock(
    clockId: string,
    klydoId: string,
    clockVersion: number | undefined,
  ): Promise<string | null> {
    if (
      clockVersion !== undefined &&
      clockVersion >= PENDING_KLYDO_MIN_VERSION
    ) {
      const docRef = this.db.collection('machines').doc(clockId);
      const doc = await docRef.get();
      const currentPending = doc.data()?.['pendingKlydos']?.value ?? [];

      await docRef.set(
        {
          pendingKlydos: {
            value: [
              ...currentPending,
              {
                id: klydoId,
                insertTo: [{ mode: 'FEED', target: null }],
                ttl: PREVIEW_TTL_MINUTES,
                insertAt: Date.now(),
                playNow: true,
                attempts: 0,
              },
            ],
            time: firebase.firestore.FieldValue.serverTimestamp(),
            source: 'api',
          },
        },
        { merge: true },
      );

      return null;
    }

    // TODO: remove playKlydo command once all clocks are on version >= 606.9
    const commandRef = await this.db
      .collection('machines')
      .doc(clockId)
      .collection('commands')
      .add({
        name: 'playKlydo',
        time: firebase.firestore.FieldValue.serverTimestamp(),
        params: [klydoId],
        commandConfig: {
          timeToLive: PREVIEW_TTL_MINUTES,
        },
      });

    return commandRef.id;
  }

  public async writeUpdateAppCommand(clockId: string): Promise<string> {
    const commandRef = await this.db
      .collection('machines')
      .doc(clockId)
      .collection('commands')
      .add({
        name: 'updateApp',
        time: firebase.firestore.FieldValue.serverTimestamp(),
      });

    return commandRef.id;
  }

  public listenToCommandStatus(
    clockId: string,
    commandId: string,
    onUpdate: (isCompleted: boolean) => void,
  ): () => void {
    const historyUnsubscribe = this.db
      .collection('machines')
      .doc(clockId)
      .collection('commandsHistory')
      .doc(commandId)
      .onSnapshot(
        (snapshot) => {
          if (snapshot.exists) {
            onUpdate(true);
            historyUnsubscribe();
          }
        },
        (error) => {
          console.error('Error in history listener:', error);
        },
      );

    return () => {
      historyUnsubscribe();
    };
  }

  public listenToClockData(
    clockId: string,
    onUpdate: (data: {
      version?: number;
      connected?: boolean;
      idf?: string;
      collectionIds?: string[];
      // Which document produced this update. Only the machines doc carries
      // `version`, so callers must not treat a machinesReport update as "the
      // clock data has loaded".
      source: 'machines' | 'machinesReport';
    }) => void,
  ): () => void {
    const machineUnsubscribe = this.db
      .collection('machines')
      .doc(clockId)
      .onSnapshot((snapshot) => {
        // Reported even when the doc is missing, so a clock that never checked
        // in resolves as "no data" instead of leaving callers stuck on loading.
        const data = snapshot.exists ? snapshot.data() : undefined;
        const machineCollections = data?.['machineCollections']?.value;

        onUpdate({
          source: 'machines',
          version: data?.version,
          idf: data?.idf,
          // The clock's own list, so a collection the user deleted on the
          // device drops out of it and the studio stops saying "Sent".
          collectionIds: Array.isArray(machineCollections)
            ? machineCollections.map(
                (collection: { id: string }) => collection.id,
              )
            : [],
        });
      });

    const reportUnsubscribe = this.db
      .collection('machinesReport')
      .doc(clockId)
      .onSnapshot((snapshot) => {
        if (snapshot.exists) {
          const data = snapshot.data();
          const heartbeat = data?.heartbeat;
          let connected = false;

          if (heartbeat) {
            const now = new Date();
            const heartbeatDate = heartbeat.toDate
              ? heartbeat.toDate()
              : new Date(heartbeat);
            const heartbeatTimeoutMs = 5 * 60 * 1000;
            const diffMs = Math.abs(now.getTime() - heartbeatDate.getTime());
            connected = diffMs < heartbeatTimeoutMs;
          }

          onUpdate({ source: 'machinesReport', connected });
        }
      });

    return () => {
      machineUnsubscribe();
      reportUnsubscribe();
    };
  }
}

const firebaseService = new FirebaseService();
export default firebaseService;
